import { useState } from 'react';
import { Button, Input } from 'antd';
import { emailMap } from './constants';
import css from './index.module.scss';
import { EFEmail } from '@/components/UI/svg/Footer';
import { useGlobal } from '@/utils/useGlobal';
import Mailchimp from '@/components/SignUpForm/Mailchimp';
import MailchimpView from '@/components/SignUpForm/Mailchimp/BaseFormView';
import Klaviyo from '@/components/SignUpForm/Klaviyo';
import KlaviyoView from '@/components/SignUpForm/Klaviyo/BaseFormView';
import Popup from '@/components/SignUpForm/Popup';
import PolicyCheck from '@/components/PolicyCheck';
import { subscribe } from '@/service/vip';

export default function Subscribe() {
    const { useLang, useRouter, gtm } = useGlobal();
    const { locale } = useRouter();
    const [popupVisible, setPopupVisible] = useState(false);
    const { t } = useLang('glacier.teaser.register');
    const emailConf = emailMap[locale] || emailMap.us;

    const showPolicyCheck = ['uk', 'eu', 'es', 'de', 'fr', 'it', 'au'].includes(locale);
    const [checked, setChecked] = useState(!showPolicyCheck);

    const onCheckChange = (e: any) => {
        if (e.target.checked) {
            setChecked(true);
        } else {
            setChecked(false);
        }
    };

    return (
        <div className={`${css.subscribeContainer} subscribe-container`}>
            {emailConf?.type === 'klaviyo' && (
                <Klaviyo
                    extra_properties={{
                        custom_source: emailConf.source,
                        $source: emailConf.source,
                        $method_type: 'Klaviyo Form',
                        $consent_version: 'Embed default text',
                        $consent_form_id: emailConf.consent_form_id,
                    }}
                    successCallback={() => {
                        setPopupVisible(true);
                        try {
                            gtm.push({
                                event: 'uaEvent',
                                event_name: 'activity_submit',
                                button_name: t('btn_text'),
                                product_name: 'EcoFlow GLACIER',
                                status: 'success',
                            });
                            window?.fbq && window?.fbq('track', 'Lead');
                        } catch (e) {
                            console.error(e);
                        }
                    }}
                    errorCallback={() => {
                        gtm.push({
                            event: 'uaEvent',
                            event_name: 'activity_submit',
                            button_name: t('btn_text'),
                            product_name: 'EcoFlow GLACIER',
                            status: 'fail',
                        });
                    }}
                >
                    <KlaviyoView list_id={emailConf.list_id} submitText={t('btn_text')} placeholder={t('placeholder')} disabled={!checked} />
                </Klaviyo>
            )}
            {emailConf.type === 'mailchimp' && (
                <>
                    <Mailchimp
                        successCallback={() => {
                            setPopupVisible(true);
                            try {
                                gtm.push({
                                    event: 'uaEvent',
                                    event_name: 'activity_submit',
                                    button_name: t('btn_text'),
                                    product_name: 'EcoFlow GLACIER',
                                    status: 'success',
                                });
                                window?.fbq('track', 'Lead');
                            } catch (e) {
                                console.error(e);
                            }
                        }}
                        errorCallback={() => {
                            gtm.push({
                                event: 'uaEvent',
                                event_name: 'activity_submit',
                                button_name: t('btn_text'),
                                product_name: 'EcoFlow GLACIER',
                                status: 'fail',
                            });
                        }}
                    >
                        <MailchimpView
                            action={emailConf.action}
                            extraFields={emailConf.extraFields}
                            submitText={t('btn_text')}
                            placeholder={t('placeholder')}
                            disabled={!checked}
                        />
                    </Mailchimp>
                </>
            )}
            {showPolicyCheck && (
                <PolicyCheck
                    terms={locale === 'au' ? 'https://au.ecoflow.com/pages/terms-of-use-1' : ''}
                    policy={locale === 'au' ? 'https://au.ecoflow.com/pages/privacy-policy' : ''}
                    onCheckChange={onCheckChange}
                />
            )}
            {emailConf.type === 'emarsys' && (
                <EmarsysSubscribe
                    placeholder={t('placeholder')}
                    button={t('btn_text')}
                    successCallback={(value: boolean) => setPopupVisible(value)}
                    locale={locale}
                />
            )}
            <Popup
                visible={popupVisible}
                title={t('title')}
                content={t.rich('desc')}
                buttonText={t('join_text1')}
                onCancel={() => setPopupVisible(false)}
                btnLink={emailConf.popupBtnLink}
                theme="black"
            />
        </div>
    );
}

function EmarsysSubscribe({ placeholder, button, locale, successCallback }: any) {
    const regx = /^[A-Za-z\d]+([.%~`$^*&(){}<>?"+=!_\\-]*[A-Za-z\d-])*@[a-zA-Z\d-]{1,30}\.[a-zA-Z\d.%#~`$^*&(){}<>?"+=!_\\-]{1,30}$/;
    const [email, setEmail] = useState('');
    const [inputMsg, setInputMsg] = useState('');

    const onSubscribe = async () => {
        const res = await subscribe(email, locale, 'WEB_NEW_PRODUCT_SUB', {
            sector_name: `Glacier_${button}`,
            entry_position: 2,
        });
        if (res?.code === '0') {
            successCallback(true);
            setEmail('');
            setInputMsg('');
        } else {
            setInputMsg(res?.message);
        }
    };

    return (
        <div className={`${css.emarsysSubscribe} emarsys-subscribe`}>
            <div className="input-form">
                <Input
                    prefix={<EFEmail width="24px" height="24px" fill="#919191" />}
                    placeholder={placeholder}
                    onChange={(e) => setEmail(e.target.value.trim())}
                    bordered={false}
                    value={email}
                    maxLength={30}
                />
                <Button type="primary" onClick={onSubscribe} disabled={!regx.test(email)}>
                    {button}
                </Button>
            </div>
            <div className="tips">{inputMsg}</div>
        </div>
    );
}
